import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Star, TrendingUp, ExternalLink } from 'lucide-react';
import { fetchSubmissionTiers, submitTool } from '../api/client';

const tierIcon = {
  free: Check,
  featured: Star,
  sponsored: TrendingUp,
};

export default function SubmitPage() {
  const [search] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [tier, setTier] = useState('free');
  const [form, setForm] = useState({
    name: '', website_url: '', submitter_email: '',
    primary_category: '', short_description: '', resource_type: 'tool',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const cancelled = search.get('cancelled') === '1';

  useEffect(() => {
    fetchSubmissionTiers().then(setTiers).catch(() => setTiers([]));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const payload = {
        ...form,
        tier,
        origin_url: window.location.origin,
      };
      const res = await submitTool(payload);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        setMsg({ ok: true, text: "Submitted. We will review your listing and add it to the tree soon." });
        setForm({ name: '', website_url: '', submitter_email: '', primary_category: '', short_description: '', resource_type: 'tool' });
      }
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-container" data-testid="submit-page">
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Submit a tool</span>
      </div>
      <h1 className="page-title">Submit your AI tool</h1>
      <p className="muted" style={{ marginBottom: 24, maxWidth: 640 }}>
        Get listed in an OSINT-style tree explored by AI enthusiasts, builders, and creators.
        Free tier ships on approval — paid tiers unlock featured placement and sponsored badges instantly.
      </p>

      {cancelled && (
        <div className="admin-block" style={{ borderColor: '#ef4444', marginBottom: 20 }} data-testid="submit-cancelled">
          Payment was cancelled — your submission was saved but not activated. You can retry any time by re-submitting.
        </div>
      )}

      <div className="tier-grid" data-testid="tier-grid">
        {tiers.map((t) => {
          const Icon = tierIcon[t.slug] || Check;
          return (
            <button
              key={t.slug}
              type="button"
              onClick={() => setTier(t.slug)}
              className={`tier-card ${tier === t.slug ? 'active' : ''}`}
              data-testid={`tier-${t.slug}`}
            >
              <div className="tier-icon"><Icon size={18} /></div>
              <div className="tier-title">{t.name}</div>
              <div className="tier-price">{t.price === 0 ? 'Free' : `$${t.price}`}</div>
              <div className="tier-desc">{t.description}</div>
            </button>
          );
        })}
      </div>

      <form className="admin-edit-form" onSubmit={submit} style={{ marginTop: 24 }} data-testid="submit-form">
        <label className="field">
          <span>Tool name *</span>
          <input required value={form.name} onChange={(e) => update('name', e.target.value)} data-testid="submit-name" />
        </label>
        <label className="field">
          <span>Website URL *</span>
          <input required type="url" value={form.website_url} onChange={(e) => update('website_url', e.target.value)} data-testid="submit-website" />
        </label>
        <label className="field">
          <span>Your email (so we can notify you when it&apos;s live)</span>
          <input type="email" value={form.submitter_email} onChange={(e) => update('submitter_email', e.target.value)} data-testid="submit-email" />
        </label>
        <div className="field-row">
          <label className="field">
            <span>Category *</span>
            <input required value={form.primary_category} onChange={(e) => update('primary_category', e.target.value)} placeholder="e.g. Text & Writing" />
          </label>
          <label className="field">
            <span>Resource type</span>
            <select value={form.resource_type} onChange={(e) => update('resource_type', e.target.value)}>
              <option value="tool">Tool</option>
              <option value="agent">Agent</option>
              <option value="gpt">Custom GPT</option>
              <option value="mcp">MCP Server</option>
              <option value="prompt">Prompt</option>
              <option value="course">Course</option>
              <option value="workflow">Workflow</option>
              <option value="api">API</option>
              <option value="community">Community</option>
              <option value="newsletter">Newsletter</option>
            </select>
          </label>
        </div>
        <label className="field">
          <span>Short description</span>
          <textarea rows={3} value={form.short_description} onChange={(e) => update('short_description', e.target.value)} />
        </label>

        {msg && (
          <div className={msg.ok ? 'success-msg' : 'error-msg'} data-testid="submit-message">{msg.text}</div>
        )}
        <div className="form-actions">
        <Link to="/" className="pill-btn">Cancel</Link>
        <button type="submit" className="pill-btn primary lg" disabled={busy} data-testid="submit-btn">
          {busy
            ? '…'
            : tier === 'free'
            ? 'Submit for review'
            : `Pay & submit ($${tiers.find((t) => t.slug === tier)?.price || 0})`}
          {tier !== 'free' && <ExternalLink size={13} />}
        </button>
        </div>
      </form>
    </div>
  );
}
